import { useCallback } from "react";
import { Mic, MicOff } from "lucide-react";
import { useSTT } from "@/hooks/useVoice";

/**
 * Textarea with a built-in voice dictation button.
 * Browser-native speech recognition — no data leaves the device.
 * Falls back to a plain textarea on unsupported browsers.
 */
export default function VoiceTextarea({
  value,
  onChange,
  rows = 3,
  placeholder,
  ariaLabel,
  testid,
  micTestid,
  className = "",
  disabled = false,
  onKeyDown,
}) {
  const append = useCallback(
    (chunk) => {
      const next =
        !value || /[\s\n]$/.test(value)
          ? `${value || ""}${chunk}`
          : `${value} ${chunk}`;
      onChange(next);
    },
    [value, onChange]
  );

  const { supported, listening, toggle } = useSTT({ onAppend: append });

  return (
    <div className="weave-voice-textarea-wrap">
      <textarea
        className={`weave-input ${className}`}
        rows={rows}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        aria-label={ariaLabel}
        disabled={disabled}
        data-testid={testid}
      />
      {supported && (
        <button
          type="button"
          onClick={toggle}
          className={`weave-mic-btn ${listening ? "is-listening" : ""}`}
          aria-label={listening ? "Stop dictation" : "Start dictation"}
          title={listening ? "Stop dictation" : "Speak your answer"}
          disabled={disabled}
          data-testid={micTestid}
        >
          {listening ? <MicOff size={14} /> : <Mic size={14} />}
          <span className="text-xs font-medium">
            {listening ? "Listening…" : "Speak"}
          </span>
        </button>
      )}
    </div>
  );
}
